import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FileText, AlertCircle, CheckCircle2, Loader2, Trash2, 
  ShieldAlert, Activity, Database, X, FileArchive, Layers, Info, ShieldCheck, Play
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { sellerService } from '../../../services/sellerService';
import toast from 'react-hot-toast';
import TierUpgradeModal from './TierUpgradeModal';
import JSZip from 'jszip';
import { inventoryUploadService } from '../../../services/inventoryUploadService';
import { useAuth } from '../../../contexts/AuthContext';

export default function InventoryUploadTab({ sellerAccount, setActiveTab, onAccountUpdate }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const [preflighting, setPreflighting] = useState(null);
  
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [jobType, setJobType] = useState('components');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingZip, setIsProcessingZip] = useState(false);
  
  // Progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, scanning, success, error

  // Review Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewJob, setReviewJob] = useState(null);
  const [stagedRecords, setStagedRecords] = useState([]);
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewLimit, setReviewLimit] = useState(10);
  const [totalReviewItems, setTotalReviewItems] = useState(0);
  const [reviewLoading, setReviewLoading] = useState(false);

  const isEntryTier = sellerAccount?.tier === 'entry';
  const tierInfo = sellerService.getTierInfo(sellerAccount?.tier);

  useEffect(() => {
    if (showReviewModal && reviewJob) {
      fetchStagedRecords(reviewJob.id, reviewJob.job_type, reviewPage, reviewLimit);
    }
  }, [showReviewModal, reviewJob, reviewPage, reviewLimit]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase.from('categories').select('id, name').order('name');
      setCategories(data || []);
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isEntryTier && sellerAccount?.id) {
      fetchUploads();
    } else {
      setLoading(false);
    }
  }, [sellerAccount?.id, isEntryTier]);

  const fetchUploads = async () => {
    if (!sellerAccount?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('upload_jobs')
        .select('*')
        .eq('seller_id', sellerAccount.id) 
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const processFile = async (selectedFile) => {
    if (!selectedFile) return;

    const allowedExtensions = ['.csv', '.json', '.zip'];
    const fileName = selectedFile.name.toLowerCase();
    const hasAllowedExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!hasAllowedExtension) {
      toast.error('Invalid file type. Please select a .csv, .json, or .zip file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (fileName.endsWith('.zip')) {
      setIsProcessingZip(true);
      try {
        const zip = new JSZip();
        const contents = await zip.loadAsync(selectedFile);
        
        const files = Object.keys(contents.files).filter(name => {
           const f = contents.files[name];
           const lowerName = name.toLowerCase();
           return !f.dir && !lowerName.includes('__macosx') && !lowerName.includes('.ds_store') && !lowerName.startsWith('.');
        });
        
        const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.stl', '.glb'];
        const invalidFiles = files.filter(name => {
          const lowerName = name.toLowerCase();
          return !validExtensions.some(ext => lowerName.endsWith(ext));
        });

        if (invalidFiles.length > 0) {
          toast.error(`ZIP rejected: contains non-image file ${invalidFiles[0]}`);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        
        if (files.length === 0) {
           toast.error('ZIP file contains no valid images or models.');
           if (fileInputRef.current) fileInputRef.current.value = '';
           return;
        }
        toast.success(`Validated ZIP: ${files.length} valid files found.`);
      } catch (err) {
        toast.error('Failed to read ZIP file structure.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      } finally {
        setIsProcessingZip(false);
      }
    }

    setFile(selectedFile);
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading && !isProcessingZip) {
      if (!isDragging) setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (uploading || isProcessingZip) return;

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCategory) {
      toast.error('Please select a file and a category.');
      return;
    }

    // NEW: Enforce 50 file limit
    if (uploads.length >= 50) {
      toast.error('You have reached the limit of 50 upload jobs. Please delete old records to upload more.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('uploading');

    try {
      const uploadRes = await inventoryUploadService.uploadFile(file, jobType, (progress) => {
        setUploadProgress(progress);
        if (progress === 100) setUploadStatus('scanning');
      });

      setUploadStatus('success');
      toast.success('File scanned and stored safely.');
      
      setTimeout(() => {
        setFile(null);
        setSelectedCategory('');
        setShowUploadModal(false);
        setUploadStatus('idle');
        setUploadProgress(0);
        fetchUploads();
      }, 2000);

    } catch (err) {
      setUploadStatus('error');
      toast.error(err.message || 'Failed to upload file');
      // fetchUploads() removed from timeout to keep modal in error state so user can see what happened
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleResetUpload = () => {
    setUploadStatus('idle');
    setUploadProgress(0);
    setFile(null);
  };

  const fetchStagedRecords = async (jobId, jobType, page, limit) => {
    if (!jobId) return;
    setReviewLoading(true);
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      const tableName = jobType === 'products' ? 'staged_products' : 'staged_components';

      const { count, error: countError } = await supabase
        .from(tableName)
        .select('id', { count: 'exact', head: true })
        .eq('job_id', jobId);

      if (countError) throw countError;
      setTotalReviewItems(count || 0);

      if (count === 0) {
        setStagedRecords([]);
        return;
      }

      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('job_id', jobId)
        .range(from, to)
        .order('row_number', { ascending: true });

      if (error) throw error;
      setStagedRecords(data || []);
    } catch (err) {
      console.error('Error fetching staged records:', err);
      toast.error('Failed to load preflight records.');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReviewRecordsClick = (job) => {
    setReviewJob(job);
    setReviewPage(1);
    setStagedRecords([]);
    setShowReviewModal(true);
  };

  const handleDelete = async (fileName, jobId, storagePath) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) return;

    try {
      // 1. Physically delete from Supabase Storage if path exists
      if (storagePath) {
        const { error: storageError } = await supabase.storage
          .from('seller-uploads')
          .remove([storagePath]);
        
        if (storageError) {
          console.warn('Could not delete physical file, but continuing with record deletion:', storageError);
        }
      }

      // 2. Delete database record
      if (jobId) {
        await supabase.from('upload_jobs').delete().eq('id', jobId);
      }
      
      toast.success('Record and file deleted successfully');
      fetchUploads();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete record');
    }
  };

  const handlePreflight = async (job) => {
    setPreflighting(job.id);
    try {
      // 1. Immediately update the database status to 'processing' before starting the fetch request
      const { error: initialUpdateError } = await supabase
        .from('upload_jobs')
        .update({ status: 'processing', updated_at: new Date().toISOString() })
        .eq('id', job.id);
        
      if (initialUpdateError) throw initialUpdateError;

      // 2. Instantly update the local UI state so the seller sees 'Processing' immediately
      setUploads(prev => prev.map(u => u.id === job.id ? { ...u, status: 'processing' } : u));

      const { data: { session } } = await supabase.auth.getSession();
      
      // Fetch the dynamically configured workflow endpoint and secret key from Server Management
      const { data: workflowConfig, error: workflowError } = await supabase
        .rpc('get_workflow_endpoint', { p_action_key: 'seller_upload_preflight' });
        
      if (workflowError) {
        console.error('Error fetching workflow config:', workflowError);
      }
      
      let endpoint = workflowConfig?.service_url;
      let apiSecret = workflowConfig?.api_secret || null;
      
      // Fallback just in case the workflow isn't defined or a service isn't assigned
      if (!endpoint) {
        console.warn('No 3rd Party API Endpoint assigned. Falling back to internal route.');
        endpoint = job.job_type === 'products' ? `/api/v1/product-uploads/${job.id}/preflight` : `/api/v1/component-uploads/${job.id}/preflight`;
      } else {
        // Dynamically route to products or components endpoint based on job_type
        if (job.job_type === 'components') {
          endpoint = endpoint.replace('/v1/seller/products', '/v1/seller/components');
        } else {
          endpoint = endpoint.replace('/v1/seller/components', '/v1/seller/products');
        }
        
        // Ensure the browser always fetches the same-origin relative proxy path instead of trying to resolve private VPC hostnames!
        if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
          if (endpoint.includes('/v1/seller')) {
            endpoint = '/v1/seller' + endpoint.split('/v1/seller')[1];
          }
        }
      }

      // 2. Invoke the API
      // Use the API Secret from the database if available, otherwise fallback to the user's session token
      const authHeader = apiSecret ? `Bearer ${apiSecret}` : (session ? `Bearer ${session.access_token}` : '');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': authHeader 
        },
        body: JSON.stringify({
          job_id: job.id,
          job_type: job.job_type,
          file_url: job.file_url,
          seller_id: job.seller_id
        })
      });

      if (!response.ok) {
        // Commented out raw errorText parsing to prevent revealing SQL INSERT statements in UI or console.
        // For root cause analysis at a later date, uncomment the lines below:
        // const errorText = await response.text().catch(() => '');
        // throw new Error(`API Endpoint rejected the request (Status ${response.status}): ${errorText}`);
        
        throw new Error(`API Endpoint rejected the request (Status ${response.status})`);
      }
      
      toast.success('Upload sent for processing successfully.');
      fetchUploads();
    } catch (err) {
      // Upon error, immediately update the database status to 'error' (Error Loading) so they can try again
      await supabase
        .from('upload_jobs')
        .update({ status: 'error', error_log: err.message || 'Internal preflight crash' })
        .eq('id', job.id);
        
      // Sychronize local state
      setUploads(prev => prev.map(u => u.id === job.id ? { ...u, status: 'error' } : u));

      // Commented out raw crash details logging to protect database schemas.
      // For developer root-cause analysis, uncomment the line below:
      // console.error('Preflight critical crash details:', err);
      
      console.error('Preflight critical crash occurred. Status Code: 500');
      toast.error(`Preflight trigger failed: ${err.message || 'Internal Server Error (500)'}`, { duration: 5000 });
    } finally {
      setPreflighting(null);
    }
  };

  const handleSubmitForApproval = async (job) => {
    const isReload = job.status === 'completed';
    const loadingToast = toast.loading(isReload ? 'Triggering catalog reload...' : 'Submitting for approval...');
    try {
        if (isReload) {
            const { data: { session } } = await supabase.auth.getSession();
            
            // Fetch the dynamically configured workflow endpoint and secret key from Server Management
            const { data: workflowConfig, error: workflowError } = await supabase
              .rpc('get_workflow_endpoint', { p_action_key: 'seller_upload_preflight' });
              
            if (workflowError) {
              console.error('Error fetching workflow config:', workflowError);
            }
            
            let endpoint = workflowConfig?.service_url;
            let apiSecret = workflowConfig?.api_secret || null;
            
            if (endpoint) {
              // Dynamically route to products or components endpoint based on job_type
              if (job.job_type === 'components') {
                endpoint = endpoint.replace('/v1/seller/products', '/v1/seller/components');
              } else {
                endpoint = endpoint.replace('/v1/seller/components', '/v1/seller/products');
              }
              
              // Ensure the browser always fetches the same-origin relative proxy path instead of trying to resolve private VPC hostnames!
              if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
                if (endpoint.includes('/v1/seller')) {
                  endpoint = '/v1/seller' + endpoint.split('/v1/seller')[1];
                }
              }
              
              // Dynamically convert /preflight path to /submit or /reload
              if (endpoint.includes('/preflight')) {
                endpoint = endpoint.replace('/preflight', '/reload');
              } else if (!endpoint.includes('/submit') && !endpoint.includes('/reload')) {
                endpoint = `${endpoint}/${job.id}/reload`;
              }
            } else {
              // Fallback just in case the workflow isn't defined or a service isn't assigned
              endpoint = job.job_type === 'products' 
                ? `/api/v1/product-uploads/${job.id}/reload` 
                : `/api/v1/component-uploads/${job.id}/reload`;
            }

            const authHeader = apiSecret ? `Bearer ${apiSecret}` : (session ? `Bearer ${session.access_token}` : '');

            // Securely invoke the 3rd party submit/reload pipeline API!
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': authHeader 
              },
              body: JSON.stringify({
                job_id: job.id,
                job_type: job.job_type,
                file_url: job.file_url,
                seller_id: job.seller_id
              })
            });

            if (!response.ok) {
              throw new Error(`API Endpoint rejected submission (Status ${response.status})`);
            }
        }

        // Update database record status
        const { error } = await supabase
          .from('upload_jobs')
          .update({ 
            status: 'submitted', 
            updated_at: new Date().toISOString() 
          })
          .eq('id', job.id);
        
        if (error) throw error;
        
        toast.dismiss(loadingToast);
        toast.success(isReload ? 'Catalog reload triggered successfully!' : 'Successfully submitted for approval!');
        fetchUploads();
    } catch (e) {
        console.error('Submission error:', e);
        toast.dismiss(loadingToast);
        toast.error(isReload ? 'Failed to trigger catalog reload.' : 'Failed to submit for approval.');
    }
  };

  const [reportingId, setReportingId] = useState(null);

  const handleAutoReportError = async (job) => {
    if (reportingId === job.id) return;
    setReportingId(job.id);
    const loadingToast = toast.loading('Submitting error report to support...');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not logged in');

      const errMsg = typeof job.error_log === 'string' ? job.error_log : (job.error_log?.message || JSON.stringify(job.error_log));
      
      const { error } = await supabase.rpc('auto_report_preflight_error', {
        p_job_id: job.id,
        p_error_msg: errMsg
      });

      if (error) throw error;

      // Update local state instantly so the seller sees 'Error Reported' immediately
      setUploads(prev => prev.map(u => u.id === job.id ? { ...u, status: 'error_reported' } : u));

      toast.dismiss(loadingToast);
      toast.success('Error auto-reported successfully! Support notified.');
    } catch (err) {
      console.error('Error reporting support request:', err);
      toast.dismiss(loadingToast);
      toast.error('Failed to auto-report error. Please contact support@opengadget.com.');
    } finally {
      setReportingId(null);
    }
  };

  const getStatusBadge = (upload) => {
    const status = upload?.status;
    const errorLog = upload?.error_log;
    const normalizedStatus = status?.toLowerCase();
    
    switch (normalizedStatus) {
      case 'completed':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold uppercase">Success</span>;
      case 'processing':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-bold uppercase animate-pulse">Processing</span>;
      case 'new':
      case 'pending':
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold uppercase">Ready for Pre-flight</span>;
      case 'verified':
        return <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-bold uppercase">Verified</span>;
      case 'error_reported':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-[10px] font-bold uppercase">Error Reported</span>;
      case 'submitted':
        return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold uppercase animate-pulse">Submitted</span>;
      case 'error':
      case 'failed':
        const errMsg = typeof errorLog === 'string' ? errorLog : (errorLog?.message || JSON.stringify(errorLog || {}));
        const isReporting = reportingId === upload.id;
        return (
          <div className="group relative inline-block">
             <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase flex items-center gap-1 cursor-help">
               Error Loading <Info size={10} />
             </span>
             {errorLog && (
               <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-gray-800 text-white text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 text-center space-y-2 pointer-events-auto">
                 <div className="font-semibold text-red-400 break-words leading-relaxed">
                   {errMsg}
                 </div>
                 <div className="border-t border-gray-700 pt-1.5 text-gray-300">
                   Please contact support or click below to auto-report this error.
                 </div>
                 <button
                   onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     handleAutoReportError(upload);
                   }}
                   disabled={isReporting}
                   className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded transition-colors text-[9px] flex items-center justify-center gap-1 cursor-pointer"
                 >
                   {isReporting ? 'Reporting...' : '🔔 Auto-Report Error'}
                 </button>
               </div>
             )}
          </div>
        );
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[10px] font-bold uppercase">{status}</span>;
    }
  };

  if (isEntryTier) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-border p-8 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldAlert size={32} />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">Upgrade Required</h2>
          <p className="text-muted-foreground mb-8">
            The Bulk Inventory Upload feature is available for Mid-Tier and Platinum sellers. 
          </p>
          <Button onClick={() => setShowUpgradeModal(true)}>Upgrade Your Plan</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
        <div className="p-6 border-b border-border flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Database size={20} className="text-primary" />
              Inventory Upload Hub
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Upload inventory files or media bundles. Trigger DataFabric for unzipping and import.</p>
          </div>
          
          <div className="flex items-center gap-3">
             <Button variant="outline" size="sm" onClick={fetchUploads} disabled={loading || uploading}>Refresh List</Button>
              <Button className="cursor-pointer flex items-center gap-2" disabled={uploading} onClick={() => setShowUploadModal(true)}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading...' : 'Upload Files'}
              </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-muted/30 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-4">File Name</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Records Verified</th>
                <th className="px-6 py-4">Size</th>
                <th className="px-6 py-4">Uploaded</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" /></td></tr>
              ) : uploads.length === 0 ? (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-muted-foreground italic">No uploads found.</td></tr>
              ) : (
                uploads.map((upload) => {
                  const rawFileName = upload.filename || upload.name || '';
                  const nameWithoutPath = rawFileName.split('/').pop();
                  // Remove starting timestamp like 171536483_
                  let currentFileName = nameWithoutPath.replace(/^\d{10,13}_/, '');
                  // Remove CAT_XXXX_FILE_ prefix if it exists
                  currentFileName = currentFileName.replace(/^CAT_[A-Z0-9-]+_FILE_/i, '');
                  
                  const isZip = currentFileName.toLowerCase().endsWith('.zip');
                  const isPreflighting = preflighting === upload.id;
                  const normalizedStatus = upload.status?.toLowerCase();
                  
                  return (
                    <tr key={upload.id} className="hover:bg-muted/10 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium flex items-center gap-2">
                        {isZip ? <FileArchive size={16} className="text-amber-500 shrink-0" /> : <FileText size={16} className="text-blue-500 shrink-0" />}
                        <span className="break-all">{currentFileName}</span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-0.5 inline-flex text-[10px] leading-5 font-bold uppercase rounded-full ${
                          upload.job_type === 'components' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {upload.job_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{getStatusBadge(upload)}</td>
                      <td className="px-6 py-4 text-sm">
                        {normalizedStatus === 'verified' && upload.record_count > 0 ? (
                          <button
                            onClick={() => handleReviewRecordsClick(upload)}
                            className="text-green-600 font-bold hover:underline cursor-pointer focus:outline-none flex items-center gap-1"
                          >
                            <span>{upload.record_count} records</span>
                            <Info size={12} className="text-green-500" />
                          </button>
                        ) : (
                          <span className="font-semibold text-foreground">
                            {upload.record_count === null || upload.record_count === undefined ? '-' : `${upload.record_count} records`}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{(upload.file_size / 1024).toFixed(2)} KB</td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{new Date(upload.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {(normalizedStatus === 'new' || normalizedStatus === 'pending' || normalizedStatus === 'failed' || normalizedStatus === 'error') && (
                             <Button variant="outline" size="sm" className="text-xs whitespace-nowrap" onClick={() => handlePreflight(upload)} disabled={isPreflighting}>
                               {isPreflighting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Activity size={12} className="mr-1" />} Pre-flight
                             </Button>
                          )}
                          {(normalizedStatus === 'completed' || normalizedStatus === 'verified') && (
                             <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs whitespace-nowrap" onClick={() => handleSubmitForApproval(upload)}>
                               <Database className="w-3 h-3 mr-1" /> {normalizedStatus === 'completed' ? 'Reload' : 'Submit for Approval'}
                             </Button>
                          )}
                          <button onClick={() => handleDelete(currentFileName, upload.id, upload.storage_path)} className="p-2 text-muted-foreground hover:text-destructive transition-colors ml-2" disabled={isPreflighting}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
           <div className="bg-white rounded-xl shadow-xl w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center p-6 border-b">
                 <h3 className="text-lg font-bold">Upload Inventory Files</h3>
                 {!uploading && <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>}
              </div>
              <div className="p-6 space-y-6">
                 {!uploading && uploadStatus === 'idle' ? (
                   <>
                    <div className="flex gap-4">
                      <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${jobType === 'products' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200'}`}>
                          <input type="radio" className="hidden" name="jobType" value="products" checked={jobType === 'products'} onChange={e => setJobType(e.target.value)} />
                          <Database size={18} /><span className="font-semibold">Products</span>
                      </label>
                      <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${jobType === 'components' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200'}`}>
                          <input type="radio" className="hidden" name="jobType" value="components" checked={jobType === 'components'} onChange={e => setJobType(e.target.value)} />
                          <Layers size={18} /><span className="font-semibold">Components</span>
                      </label>
                    </div>
                    <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="w-full border rounded-lg p-2 text-sm bg-gray-50 focus:bg-white transition-colors">
                      <option value="">Select a category...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${isDragging ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-gray-300'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                      <input type="file" id="csv-upload-modal" className="hidden" accept=".csv,.json,.zip" onChange={handleFileChange} disabled={uploading} ref={fileInputRef} />
                      <label htmlFor="csv-upload-modal" className="block cursor-pointer">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-primary/10 text-primary">
                          {isProcessingZip ? <Loader2 className="w-8 h-8 animate-spin" /> : (file?.name.toLowerCase().endsWith('.zip') ? <FileArchive size={32} /> : <FileText size={32} />)}
                        </div>
                        <p className="text-lg font-semibold mb-1">{isProcessingZip ? 'Validating...' : (file ? file.name : 'Choose a file (.csv, .json, .zip)')}</p>
                      </label>
                      {file && !isProcessingZip && (
                        <div className="flex justify-center gap-3 mt-4">
                          <Button variant="outline" onClick={() => setFile(null)}>Clear</Button>
                          <Button onClick={handleUpload} disabled={!selectedCategory}>{uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Start Upload</Button>
                        </div>
                      )}
                    </div>
                   </>
                 ) : (
                   <div className="py-12 flex flex-col items-center justify-center text-center">
                      <div className="w-20 h-20 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-6">
                         {uploadStatus === 'uploading' && <Upload className="w-10 h-10 animate-bounce" />}
                         {uploadStatus === 'scanning' && <ShieldCheck className="w-10 h-10 animate-pulse text-green-600" />}
                         {uploadStatus === 'success' && <CheckCircle2 className="w-10 h-10 text-green-500" />}
                         {uploadStatus === 'error' && <AlertCircle className="w-10 h-10 text-destructive" />}
                      </div>
                      <h4 className="text-xl font-bold mb-2">
                        {uploadStatus === 'uploading' && 'Transferring Data...'}
                        {uploadStatus === 'scanning' && 'Scanning for Threats...'}
                        {uploadStatus === 'success' && 'Upload Complete!'}
                        {uploadStatus === 'error' && 'Upload Failed'}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-8">
                        {uploadStatus === 'uploading' && `Moving ${file?.name} to secure vault.`}
                        {uploadStatus === 'scanning' && 'Running automated security integrity checks.'}
                        {uploadStatus === 'success' && 'File stored safely. You can now run Pre-flight.'}
                      </p>
                      <div className="w-full max-w-md bg-muted rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadStatus === 'scanning' ? 100 : uploadProgress}%` }} />
                      </div>
                      
                      {uploadStatus === 'error' && (
                        <Button onClick={handleResetUpload} className="mt-8">
                           Try Again
                        </Button>
                      )}
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}

      {showReviewModal && reviewJob && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
           <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center p-6 border-b shrink-0">
                 <div>
                   <h3 className="text-lg font-bold">Review Preflight Records</h3>
                   <p className="text-xs text-muted-foreground mt-0.5 font-medium">Filename: {reviewJob.filename.split('/').pop().replace(/^\d+_/,'')} | Type: {reviewJob.job_type}</p>
                 </div>
                 <button onClick={() => setShowReviewModal(false)} className="text-gray-400 hover:text-gray-600">
                   <X size={20}/>
                 </button>
              </div>
              
              <div className="p-6 overflow-y-auto flex-1">
                 <div className="overflow-x-auto border border-border rounded-lg bg-gray-50">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-muted/30 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3">Row</th>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Price</th>
                          <th className="px-4 py-3">Stock</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Validation Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border bg-white text-sm">
                        {reviewLoading ? (
                          <tr><td colSpan="6" className="px-4 py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" /></td></tr>
                        ) : stagedRecords.length === 0 ? (
                          <tr><td colSpan="6" className="px-4 py-12 text-center text-muted-foreground italic">No preflight records found.</td></tr>
                        ) : (
                          stagedRecords.map((record) => {
                            const name = record.name || record.custom_name || `Item #${record.row_number}`;
                            const hasErrors = record.errors && Object.keys(record.errors).length > 0;
                            return (
                              <tr key={record.id} className="hover:bg-muted/5 transition-colors">
                                <td className="px-4 py-3 font-semibold text-muted-foreground">{record.row_number}</td>
                                <td className="px-4 py-3 font-medium text-foreground truncate max-w-[200px]" title={name}>{name}</td>
                                <td className="px-4 py-3">${Number(record.price || 0).toFixed(2)}</td>
                                <td className="px-4 py-3">{record.stock_quantity ?? '-'}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                    record.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {record.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-xs text-red-600 break-words max-w-[300px]">
                                  {hasErrors ? (
                                    typeof record.errors === 'string' 
                                      ? record.errors 
                                      : (record.errors.message || JSON.stringify(record.errors))
                                  ) : (
                                    <span className="text-green-600 font-semibold">Valid</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                 </div>
              </div>

              {!reviewLoading && stagedRecords.length > 0 && (
                <div className="p-4 border-t shrink-0 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50">
                  <span className="text-xs text-muted-foreground font-semibold">
                    Showing {((reviewPage - 1) * reviewLimit) + 1} to {Math.min(reviewPage * reviewLimit, totalReviewItems)} of {totalReviewItems} items
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setReviewPage(prev => Math.max(prev - 1, 1))} 
                      disabled={reviewPage === 1}
                    >
                      Previous
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setReviewPage(prev => Math.min(prev + 1, Math.ceil(totalReviewItems / reviewLimit)))} 
                      disabled={reviewPage >= Math.ceil(totalReviewItems / reviewLimit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
           </div>
        </div>
      )}
    </div>
  );
}
